import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { readCookie, writeCookie } from "../../lib/cookies"

const TABLET_BREAKPOINT = 768

export default class extends Controller {
  static targets = [
    "displayMode", "sorter", "sortPopper", "size", "sizePopper", "clearSizes",
    "brandPopper", "brandBody", "brand", "clearBrands",
    "color", "colorPopper", "clearColors"
  ]

  connect() {
    this.element.dataset.catalogFiltersReady = "true"
    this.isMobileHeader = Boolean(this.element.closest("#header-filters"))
    this.isMobileViewport = window.innerWidth < TABLET_BREAKPOINT
    this.resizeHandler = () => this.handleResize()
    window.addEventListener("resize", this.resizeHandler)
    this.syncDisplayMode()
  }

  disconnect() {
    this.brandRequest?.abort()
    window.removeEventListener("resize", this.resizeHandler)
    this.closeColors()
    this.closeSizes()
    this.closeSort()
    delete this.element.dataset.catalogFiltersReady
    delete this.element.dataset.catalogDisplayMode
  }

  toggleSort(event) {
    event.preventDefault()
    if (!this.hasSortPopperTarget) return

    if (this.sortPopperTarget.style.visibility === "visible") {
      this.closeSort()
      return
    }

    this.closeBrands()
    this.closeSizes()
    this.closeColors()
    this.sortPopperTarget.style.visibility = "visible"
    event.currentTarget.setAttribute("aria-expanded", "true")
    event.currentTarget.closest(".filter")?.classList.add("active")
  }

  closeSort(event) {
    if (!this.hasSortPopperTarget) return
    const filter = this.sortPopperTarget.closest(".filter")
    if (event && filter.contains(event.target) && !event.target.classList.contains("popper-backdrop")) return

    this.sortPopperTarget.style.visibility = "hidden"
    filter.classList.toggle("active", filter.classList.contains("has-addon"))
    filter.querySelector("[aria-expanded]")?.setAttribute("aria-expanded", "false")
  }

  async toggleBrands(event) {
    event.preventDefault()
    this.closeColors()
    this.closeSizes()
    this.closeSort()
    delete this.element.dataset.catalogFiltersError
    const trigger = event.currentTarget
    if (this.brandPopperTarget.style.visibility === "visible") {
      this.closeBrands()
      return
    }

    this.brandRequest?.abort()
    const request = new AbortController()
    this.brandRequest = request

    try {
      const response = await fetch(trigger.dataset.url, {
        headers: { Accept: "text/html" }, signal: request.signal
      })
      if (!response.ok) throw new Error(`Brand filters failed with status ${response.status}`)

      this.brandBodyTarget.innerHTML = await response.text()
      this.brandPopperTarget.style.visibility = "visible"
      trigger.setAttribute("aria-expanded", "true")
      trigger.closest(".filter")?.classList.add("active")
      this.updateBrandClear()
    } catch (error) {
      if (error.name !== "AbortError") {
        this.element.dataset.catalogFiltersError = error.message
        this.closeBrands()
      }
    } finally {
      if (this.brandRequest === request) this.brandRequest = null
    }
  }

  closeBrands(event) {
    if (!this.hasBrandPopperTarget) return
    const filter = this.brandPopperTarget.closest(".filter")
    if (event && filter.contains(event.target) && !event.target.classList.contains("popper-backdrop")) return

    this.brandRequest?.abort()
    this.brandRequest = null
    this.brandPopperTarget.style.visibility = "hidden"
    this.brandBodyTarget.replaceChildren()
    filter.classList.toggle("active", filter.classList.contains("has-addon"))
    filter.querySelector("[aria-expanded]")?.setAttribute("aria-expanded", "false")
  }

  updateBrandClear() {
    this.clearBrandsTarget.disabled = !this.brandTargets.some(input => input.checked || input.defaultChecked)
  }

  applyBrands(event) {
    event.preventDefault()
    const brands = this.brandTargets.filter(input => input.checked).map(input => input.value)
    this.closeBrands()
    this.navigateWithFilter("brands", brands)
  }

  resetBrands(event) {
    event.preventDefault()
    this.closeBrands()
    this.navigateWithFilter("brands", [])
  }

  selectDisplayMode(event) {
    event.preventDefault()
    writeCookie("products_display_mode", event.currentTarget.dataset.col, 365)
    this.syncDisplayMode()
  }

  syncDisplayMode() {
    const products = document.getElementById("products")
    const preferredMode = readCookie("products_display_mode") || products?.dataset.displayMode || "2"
    const mode = preferredMode === "1" && window.innerWidth >= 768 ? "2" : preferredMode

    this.displayModeTargets.forEach(option => option.classList.toggle("active", option.dataset.col === mode))
    if (products) products.dataset.displayMode = mode
    this.element.dataset.catalogDisplayMode = mode
  }

  toggleSizes(event) {
    event.preventDefault()
    if (!this.hasSizePopperTarget) return

    const trigger = event.currentTarget
    if (this.sizePopperTarget.style.visibility === "visible") {
      this.closeSizes()
      return
    }

    this.closeBrands()
    this.closeColors()
    this.closeSort()
    this.restoreSizeSelection()
    this.sizePopperTarget.style.visibility = "visible"
    trigger.setAttribute("aria-expanded", "true")
    trigger.closest(".filter")?.classList.add("active")
  }

  closeSizes(event) {
    if (!this.hasSizePopperTarget) return
    const filter = this.sizePopperTarget.closest(".filter")
    if (event && filter.contains(event.target) && !event.target.classList.contains("popper-backdrop")) return

    this.restoreSizeSelection()
    this.sizePopperTarget.style.visibility = "hidden"
    filter.classList.toggle("active", filter.classList.contains("has-addon"))
    filter.querySelector("[aria-expanded]")?.setAttribute("aria-expanded", "false")
  }

  updateSizeSelection(event) {
    event.currentTarget.closest("label")?.classList.toggle("active", event.currentTarget.checked)
    this.updateSizeClear()
  }

  updateSizeClear() {
    if (!this.hasClearSizesTarget) return

    this.clearSizesTarget.disabled = !this.sizeTargets.some(input => input.checked || input.defaultChecked)
  }

  applySizeFilter(event) {
    event.preventDefault()
    const sizes = this.sizeTargets.filter(input => input.checked).map(input => input.value)
    this.closeSizes()
    this.navigateWithFilter("sizes", sizes)
  }

  resetSizes(event) {
    event.preventDefault()
    this.sizeTargets.forEach(input => {
      input.checked = false
      input.closest("label")?.classList.remove("active")
    })
    this.updateSizeClear()
    this.closeSizes()
    this.navigateWithFilter("sizes", [])
  }

  restoreSizeSelection() {
    this.sizeTargets.forEach(input => {
      input.checked = input.defaultChecked
      input.closest("label")?.classList.toggle("active", input.checked)
    })
    this.updateSizeClear()
  }

  toggleColors(event) {
    event.preventDefault()
    if (!this.hasColorPopperTarget) return

    const trigger = event.currentTarget
    if (this.colorPopperTarget.style.visibility === "visible") {
      this.closeColors()
      return
    }

    this.closeBrands()
    this.closeColors()
    this.closeSizes()
    this.closeSort()
    this.restoreColorSelection()
    this.colorPopperTarget.style.visibility = "visible"
    trigger.setAttribute("aria-expanded", "true")
    trigger.closest(".filter")?.classList.add("active")
  }

  closeColors(event) {
    if (!this.hasColorPopperTarget) return
    const filter = this.colorPopperTarget.closest(".filter")
    if (event && filter.contains(event.target) && !event.target.classList.contains("popper-backdrop")) return

    this.restoreColorSelection()
    this.colorPopperTarget.style.visibility = "hidden"
    filter.classList.toggle("active", filter.classList.contains("has-addon"))
    filter.querySelector("[aria-expanded]")?.setAttribute("aria-expanded", "false")
  }

  updateColorSelection(event) {
    event.currentTarget.closest("label")?.classList.toggle("active", event.currentTarget.checked)
    this.updateColorClear()
  }

  updateColorClear() {
    if (!this.hasClearColorsTarget) return

    this.clearColorsTarget.disabled = !this.colorTargets.some(input => input.checked || input.defaultChecked)
  }

  applyColorFilter(event) {
    event.preventDefault()
    const colors = this.colorTargets.filter(input => input.checked).map(input => input.value)
    this.closeColors()
    this.navigateWithFilter("colors", colors)
  }

  resetColors(event) {
    event.preventDefault()
    this.colorTargets.forEach(input => {
      input.checked = false
      input.closest("label")?.classList.remove("active")
    })
    this.updateColorClear()
    this.closeColors()
    this.navigateWithFilter("colors", [])
  }

  restoreColorSelection() {
    this.colorTargets.forEach(input => {
      input.checked = input.defaultChecked
      input.closest("label")?.classList.toggle("active", input.checked)
    })
    this.updateColorClear()
  }

  handleResize() {
    const isMobileViewport = window.innerWidth < TABLET_BREAKPOINT
    if (isMobileViewport !== this.isMobileViewport) {
      this.closeBrands()
      this.closeSizes()
      this.closeColors()
      this.closeSort()
      this.isMobileViewport = isMobileViewport
    }
    this.syncDisplayMode()
  }

  navigateWithFilter(name, values) {
    const url = new URL(window.location.href)
    url.searchParams.delete(name)
    url.searchParams.delete(`${name}[]`)
    url.searchParams.delete("page")
    values.forEach(value => url.searchParams.append(`${name}[]`, value))
    Turbo.visit(url, { frame: "sale-catalog", action: "advance" })
  }

}
